<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import VehicleCard from '@/components/vehicle/vehicle_card.vue'
import Scrollable from '@/components/scrollable.vue'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { useDebounceFn } from '@vueuse/core'

// I18n
const { t } = useI18n()

// Stores
const garageStore = useGarageStore()
const modalStore = useModalStore()

// Variables
const isExpandOpen = ref(false)
const expandVehicle = ref(null)
const mouseX = ref(0)
const mouseY = ref(0)

// Event Handlers
onMounted(async () => {
  document.addEventListener('click', closeExpandOnOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', closeExpandOnOutsideClick)
})

// Functions
function closeExpand() {
  isExpandOpen.value = false
  expandVehicle.value = null
}

function closeExpandOnOutsideClick(event) {
  if (isExpandOpen.value && !event.target.closest('.dropdown-menu')) {
    closeExpand()
  }
}

function toggleExpand(vehicle, event) {
  console.log('Toggle expand received for vehicle:', vehicle.id, 'isExpandOpen:', isExpandOpen.value);
  event.stopPropagation()
  if (isExpandOpen.value && expandVehicle.value?.id === vehicle.id) {
    closeExpand()
  } else {
    isExpandOpen.value = true
    expandVehicle.value = vehicle
    mouseX.value = event.pageX
    mouseY.value = event.pageY
    console.log('Set isExpandOpen to true, expandVehicle:', expandVehicle.value?.id, 'mouseX:', mouseX.value, 'mouseY:', mouseY.value);
  }
}

const onFileChange = (event) => {
  garageStore.updateVehicleImage(event.target.files[0])
}

const addVehicle = useDebounceFn(() => {
  modalStore.setModal(modalStore.modalTypeConstants.ADD_VEHICLE)
}, 250, { maxWait: 1000 })

</script>

<template>
  <div>
    <div class="mb-5">
      <p class="text-xl">{{ t('request_a_quote.vehicle_section.subtext[0]') }}</p>
      <p class="text-xl">{{ t('request_a_quote.vehicle_section.subtext[1]') }}</p>
      <p class="text-xl">{{ t('request_a_quote.vehicle_section.subtext[2]') }}</p>
    </div>

    <div class="flex flex-row mb-4">
      <p class="text-lg uppercase font-regular select-vehicle">{{ t('my_garage.select_vehicle') }}</p>
      <div v-if="!garageStore.maxVehicles()"
           class="flex items-center justify-center px-2 rounded-full font-regular add-vehicle shadow-md ml-4 cursor-pointer hover:opacity-90"
           @click="addVehicle">
        <span class="px-1 focus:outline-none">
         {{ t('my_garage.add_a_vehicle') }}
        </span>
        <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
             class="h-[22px] w-[22px] pr-1">
      </div>
      <div v-else class="flex items-center justify-center px-2 rounded-full font-regular add-vehicle shadow-md ml-4">
        <span class="px-1 focus:outline-none">
         {{ t('my_garage.garage_full') }}
        </span>
      </div>
    </div>

    <Scrollable :show-scroll="garageStore.userVehicles.length > 1">
      <VehicleCard v-for="vehicle in garageStore.userVehicles"
                   :key="`vehicle-parent-${vehicle.id}`"
                   :vehicle="vehicle" />
    </Scrollable>

    <transition enter-active-class="transition ease-out duration-100"
                leave-active-class="transition ease-in duration-75">
      <div v-if="isExpandOpen && expandVehicle"
           :style="{ top: `${mouseY}px`, left: `calc(${mouseX}px - 12.5rem)` }"
           class="absolute z-10 mt-2 w-48 bg-white rounded-md shadow-lg dropdown-menu">
        <ul class="z-10 w-48 bg-white rounded-md shadow-lg right-0 mr-0 overflow-hidden" @click="closeExpand">
          <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
              @click="modalStore.setModal(modalStore.modalTypeConstants.EDIT_VEHICLE, expandVehicle)">
            {{ t('my_garage.edit_vehicle') }}
          </li>
          <label class="block px-4 py-2 cursor-pointer">
            <input type="file" @change="onFileChange" class="hidden">
            <span class="text-sm font-semibold text-gray-700">{{ t('my_garage.vehicle_info_section.vehicle_image_upload') }}</span>
          </label>
          <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
              @click="garageStore.archiveVehicle(expandVehicle)">
            {{ t('my_garage.hide_vehicle') }}
          </li>
          <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
              @click="garageStore.removeVehicle(expandVehicle)">
            {{ t('my_garage.remove_from_garage') }}
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';

</style>
